import { useState, useEffect } from "react";
import { getOffenders } from "../api/OffendersApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Search, AlertTriangle, TrendingUp, Clock, ShieldAlert, Flame } from "lucide-react";
import { toast } from "sonner";
import CulsightPageLoader from "../components/ui/CulsightLoader";

const Offenders = () => {
  const [offenders, setOffenders] = useState([]);
  const [summary, setSummary] = useState({
    total_offenders: 0,
    critical: 0,
    high_risk: 0,
    medium_risk: 0,
    total_offenses: 0,
  });
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [riskLevel, setRiskLevel] = useState("");

  // Debounced search + risk level - calls backend
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOffenders(search, riskLevel);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, riskLevel]);

  const fetchOffenders = async (searchTerm, riskLevelValue) => {
    if (offenders.length === 0) {
      setLoading(true);
    } else {
      setListLoading(true);
    }

    try {
      const response = await getOffenders({
        search: searchTerm || undefined,
        risk_level: riskLevelValue || undefined,
      });

      setOffenders(response.data.offenders || []);

      setSummary(
        response.data.summary || {
          total_offenders: 0,
          critical: 0,
          high_risk: 0,
          medium_risk: 0,
          total_offenses: 0,
        }
      );
    } catch (error) {
      toast.error("Failed to load offenders");
    } finally {
      setLoading(false);
      setListLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "critical": return { color: "#991b1b", bg: "bg-red-100", text: "Critical Risk" };
      case "high": return { color: "#ef4444", bg: "bg-red-50", text: "High Risk" };
      case "medium": return { color: "#f59e0b", bg: "bg-amber-50", text: "Medium Risk" };
      case "low": return { color: "#1fa7a1", bg: "bg-teal-50", text: "Low Risk" };
      default: return { color: "#64748b", bg: "bg-gray-50", text: "Unknown" };
    }
  };

  if (loading) {
    return <CulsightPageLoader />;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="offenders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Offenders</h1>
          <p className="text-gray-500 text-sm mt-1">Track repeat phishing simulation offenders</p>
        </div>
      </div>

      {/* Stats - from backend summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: "Total Offenders", value: summary.total_offenders, icon: AlertTriangle, color: "#8c3add", filterValue: "" },
          { label: "Critical Risk", value: summary.critical, icon: Flame, color: "#991b1b", filterValue: "critical" },
          { label: "High Risk", value: summary.high_risk, icon: ShieldAlert, color: "#ef4444", filterValue: "high" },
          { label: "Medium Risk", value: summary.medium_risk, icon: TrendingUp, color: "#f59e0b", filterValue: "medium" },
          { label: "Total Offenses", value: summary.total_offenses, icon: Clock, color: "#1fa7a1", filterValue: null }
        ].map((stat, index) => (
          <Card
            key={index}
            onClick={() => {
              if (stat.filterValue === null) return;
              setRiskLevel(stat.filterValue);
            }}
            className={`border shadow-sm ${stat.filterValue !== null ? "cursor-pointer" : ""} ${riskLevel === stat.filterValue ? "border-[#8c3add] ring-1 ring-[#8c3add]" : "border-gray-100"
              }`}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1f1f23]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search - hits backend */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          placeholder="Search by name, email or department..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="offenders-search"
        />
      </div>

      {/* Table */}
      <Card className="border border-gray-100 shadow-sm" data-testid="offenders-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Offenses</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Offense Types</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Risk Level</th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Offense</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex justify-center py-12">
                        <CulsightPageLoader />
                      </div>
                    </td>
                  </tr>
                ) : offenders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-500">
                      No offenders found
                    </td>
                  </tr>
                ) : (
                  offenders.map((offender) => {
                    const risk = getRiskColor(offender.risk_level);
                    return (
                      <tr
                        key={offender.user_id}
                        className="border-b border-gray-50 hover:bg-[#8c3add]/[0.02] transition-colors"
                        data-testid={`offender-row-${offender.user_id}`}
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border-2" style={{ borderColor: risk.color }}>
                              <AvatarFallback className="bg-[#8c3add]/10 text-[#8c3add] font-semibold text-sm">
                                {(offender.user_name || "").split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-[#1f1f23]">{offender.user_name}</p>
                              <p className="text-sm text-gray-500">{offender.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-gray-600">{offender.department || "Unassigned"}</td>
                        <td className="py-4 px-6">
                          <span className="text-xl font-bold" style={{ color: risk.color }}>
                            {offender.offense_count}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex flex-wrap gap-1">
                            {[...new Set(offender.offense_types)].map((type, i) => (
                              <Badge key={i} variant="outline" className="text-xs capitalize">
                                {type.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <Badge className={`${risk.bg} border-0`} style={{ color: risk.color }}>
                            {risk.text}
                          </Badge>
                        </td>
                        <td className="py-4 px-6 text-gray-600 text-sm">
                          {offender.last_offense ? new Date(offender.last_offense).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Offenders;